import { useEffect, useMemo, useState } from 'react';
import {
  MdBolt,
  MdBusiness,
  MdFilterList,
  MdInfoOutline,
  MdEmail,
  MdNotifications,
  MdPerson,
  MdSend,
  MdSettings,
  MdTune,
} from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import PageHeader from '../../../components/owner/PageHeader';
import { usePanelToast } from '../../../context/PanelToastContext';
import {
  fetchNotificationSettings,
  fetchNotificationHistory,
  saveNotificationSettings,
  logPlatformNotification,
} from '../../../services/owner/platformApi';
import { sendNotificationTestEmail } from '../../../services/notifications/sendTestEmailApi';
import {
  channelFilterOptions,
  defaultEmailConnection,
  defaultGlobalConfig,
  defaultWhatsAppConnection,
  emailSourceOptions,
  gymReminders as initialGymReminders,
  notificationHistory as initialHistory,
  statusFilterOptions,
  typeFilterOptions,
} from './notificationsData';
import '../ownerPages.css';
import '../subscriptions/subscriptions.css';
import '../../admin/notifications/notifications.css';
import './notifications.css';

const statusBadge = {
  sent: 'owner-badge-success',
  pending: 'owner-badge-info',
  failed: 'owner-badge-warning',
};

function mergeReminders(saved, defaults) {
  const byId = Object.fromEntries((saved || []).map((r) => [r.id, r]));
  return defaults.map((d) => ({ ...d, ...byId[d.id] }));
}

function BigToggle({ checked, onChange, ariaLabel }) {
  return (
    <label className="rec-notif-big-toggle" aria-label={ariaLabel}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="rec-notif-big-toggle-slider" />
    </label>
  );
}

function ReminderRow({ item, onUpdate, onToggleExpand, expanded }) {
  const update = (patch) => onUpdate(item.id, patch);

  return (
    <div className="rec-notif-reminder-row">
      <div className="rec-notif-reminder-main">
        <BigToggle
          checked={item.enabled}
          onChange={(enabled) => update({ enabled })}
          ariaLabel={`Toggle ${item.label}`}
        />
        <div className="rec-notif-reminder-info">
          <div className="rec-notif-reminder-title">{item.label}</div>
          <div className="rec-notif-reminder-slug">{item.id}</div>
        </div>
        <div className="rec-notif-reminder-actions">
          <label className="rec-notif-channel-check">
            <input
              type="checkbox"
              checked={item.email}
              onChange={(e) => update({ email: e.target.checked })}
            />
            Email
          </label>
          <button
            type="button"
            className={`rec-notif-configure-btn${expanded ? ' active' : ''}`}
            onClick={() => onToggleExpand(item.id)}
          >
            <MdTune />
            Configure
          </button>
        </div>
      </div>

      {expanded && (
        <div className="rec-notif-config-panel">
          <div className="rec-notif-config-grid">
            {item.hasDaysBefore && (
              <div>
                <label htmlFor={`${item.id}-days`}>Days Before</label>
                <input
                  id={`${item.id}-days`}
                  type="number"
                  min="0"
                  className="owner-form-input"
                  value={item.daysBefore}
                  onChange={(e) => update({ daysBefore: Number(e.target.value) })}
                />
              </div>
            )}
            <div>
              <label htmlFor={`${item.id}-time`}>Send Time</label>
              <input
                id={`${item.id}-time`}
                type="time"
                className="owner-form-input"
                value={item.sendTime}
                onChange={(e) => update({ sendTime: e.target.value })}
              />
            </div>
            <div>
              <label>Channels</label>
              <label className="rec-notif-channel-check" style={{ marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={item.inAppLog}
                  onChange={(e) => update({ inAppLog: e.target.checked })}
                />
                In-App Log
              </label>
            </div>
            <div className="rec-notif-template-field">
              <label htmlFor={`${item.id}-template`}>Message Template</label>
              <textarea
                id={`${item.id}-template`}
                className="owner-form-input"
                value={item.template}
                onChange={(e) => update({ template: e.target.value })}
                rows={3}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const toast = usePanelToast();
  const [activeTab, setActiveTab] = useState('history');
  const [history, setHistory] = useState(initialHistory);
  const [globalConfig, setGlobalConfig] = useState(defaultGlobalConfig);
  const [gymReminders, setGymReminders] = useState(initialGymReminders);
  const [expandedId, setExpandedId] = useState('');
  const [viewTarget, setViewTarget] = useState(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectForm, setConnectForm] = useState(defaultEmailConnection);
  const [connectError, setConnectError] = useState('');
  const [connectSaving, setConnectSaving] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappForm, setWhatsappForm] = useState(defaultWhatsAppConnection);
  const [whatsappError, setWhatsappError] = useState('');
  const [whatsappSaving, setWhatsappSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [autoTestSending, setAutoTestSending] = useState(false);

  const [statusFilter, setStatusFilter] = useState('All');
  const [channelFilter, setChannelFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [appliedFilters, setAppliedFilters] = useState({
    status: 'All',
    channel: 'All',
    type: 'All',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const [settings, logs] = await Promise.all([
          fetchNotificationSettings(),
          fetchNotificationHistory(),
        ]);
        if (cancelled) return;
        setGlobalConfig({ ...defaultGlobalConfig, ...settings.globalConfig });
        setGymReminders(mergeReminders(settings.gymReminders, initialGymReminders));
        setHistory(logs);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load notifications');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredHistory = useMemo(() => {
    return history.filter((item) => {
      const statusMatch =
        appliedFilters.status === 'All' ||
        item.status === appliedFilters.status.toLowerCase();
      const channelMatch =
        appliedFilters.channel === 'All' ||
        item.channel === appliedFilters.channel.toLowerCase();
      const typeMatch = appliedFilters.type === 'All' || item.type === appliedFilters.type;
      return statusMatch && channelMatch && typeMatch;
    });
  }, [history, appliedFilters]);

  const updateGlobal = (patch) => setGlobalConfig((prev) => ({ ...prev, ...patch }));

  const updateGymReminder = (id, patch) => {
    setGymReminders((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? '' : id));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ status: statusFilter, channel: channelFilter, type: typeFilter });
  };

  const handleOpenConnectModal = () => {
    setConnectForm({
      ...(globalConfig.smtp || defaultEmailConnection),
      password: '',
    });
    setConnectError('');
    setShowConnectModal(true);
  };

  const handleConnectFieldChange = (e) => {
    const { name, value } = e.target;
    setConnectForm((prev) => ({ ...prev, [name]: value }));
    setConnectError('');
  };

  const handleConnectEmail = async (e) => {
    e.preventDefault();

    if (!connectForm.host.trim() || !connectForm.port.trim() || !connectForm.username.trim()
      || !connectForm.password.trim() || !connectForm.fromEmail.trim()) {
      setConnectError('Please fill in SMTP host, port, username, password, and from email.');
      return;
    }

    setConnectSaving(true);
    try {
      const smtp = {
        host: connectForm.host.trim(),
        port: connectForm.port.trim(),
        encryption: connectForm.encryption,
        username: connectForm.username.trim(),
        password: String(connectForm.password || '')
          .replace(/\s+/g, '')
          .replace(/[\u200B-\u200D\uFEFF]/g, ''),
        fromName: connectForm.fromName.trim() || 'GymPro Notifications',
        fromEmail: connectForm.fromEmail.trim(),
      };

      const nextConfig = {
        ...globalConfig,
        emailConnected: true,
        emailEnabled: true,
        emailSource: smtp.fromEmail,
        smtp,
      };

      await saveNotificationSettings(nextConfig, gymReminders);
      setGlobalConfig(nextConfig);
      setShowConnectModal(false);
      setConnectError('');
      toast.showSaved('Email settings');
    } catch (err) {
      setConnectError(err.message || 'Failed to save email settings');
    } finally {
      setConnectSaving(false);
    }
  };

  const handleDisconnectEmail = async () => {
    try {
      const nextConfig = {
        ...globalConfig,
        emailConnected: false,
        emailEnabled: false,
        emailSource: '',
        smtp: { ...defaultEmailConnection },
      };
      await saveNotificationSettings(nextConfig, gymReminders);
      setGlobalConfig(nextConfig);
      toast.showSaved('Email settings');
    } catch (err) {
      toast.showError(err.message || 'Failed to disconnect email');
    }
  };

  const handleOpenWhatsAppModal = () => {
    setWhatsappForm({
      ...(globalConfig.whatsapp || defaultWhatsAppConnection),
      apiKey: '',
    });
    setWhatsappError('');
    setShowWhatsAppModal(true);
  };

  const handleWhatsAppFieldChange = (e) => {
    const { name, value } = e.target;
    setWhatsappForm((prev) => ({ ...prev, [name]: value }));
    setWhatsappError('');
  };

  const handleConnectWhatsApp = async (e) => {
    e.preventDefault();

    if (!whatsappForm.phoneNumber.trim() || !whatsappForm.apiKey.trim()) {
      setWhatsappError('Please fill in business phone number and API key.');
      return;
    }

    setWhatsappSaving(true);
    try {
      const whatsapp = {
        phoneNumber: whatsappForm.phoneNumber.trim(),
        apiKey: whatsappForm.apiKey.trim(),
        businessName: whatsappForm.businessName.trim() || 'Gym CRM',
      };

      const nextConfig = {
        ...globalConfig,
        whatsappConnected: true,
        whatsappEnabled: true,
        whatsappPhone: whatsapp.phoneNumber,
        whatsapp,
      };

      await saveNotificationSettings(nextConfig, gymReminders);
      setGlobalConfig(nextConfig);
      setShowWhatsAppModal(false);
      setWhatsappError('');
      toast.showSaved('WhatsApp settings');
    } catch (err) {
      setWhatsappError(err.message || 'Failed to save WhatsApp settings');
    } finally {
      setWhatsappSaving(false);
    }
  };

  const handleDisconnectWhatsApp = async () => {
    try {
      const nextConfig = {
        ...globalConfig,
        whatsappConnected: false,
        whatsappEnabled: false,
        whatsappPhone: '',
        whatsapp: { ...defaultWhatsAppConnection },
      };
      await saveNotificationSettings(nextConfig, gymReminders);
      setGlobalConfig(nextConfig);
      toast.showSaved('WhatsApp settings');
    } catch (err) {
      toast.showError(err.message || 'Failed to disconnect WhatsApp');
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsSaving(true);
      await saveNotificationSettings(globalConfig, gymReminders);
      toast.showSaved('Notification settings');
    } catch (err) {
      toast.showError(err.message || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!globalConfig.emailConnected || !globalConfig.smtp?.host) {
      toast.showError('Connect Email first, then send a test.');
      return;
    }

    const to = String(
      globalConfig.adminEmail
      || globalConfig.smtp?.fromEmail
      || globalConfig.smtp?.username
      || '',
    ).trim();

    if (!to) {
      toast.showError('No recipient found. Set From email in Connect Email, then try again.');
      return;
    }

    try {
      setAutoTestSending(true);
      await sendNotificationTestEmail({
        smtp: globalConfig.smtp,
        to,
        subject: 'Gym CRM — test email',
        note: 'Automatic connection check from Owner → Notifications.',
      });

      await logPlatformNotification({
        recipient: to,
        gymName: 'Platform',
        type: 'test_email',
        channel: 'email',
        status: 'sent',
        message: `Test email sent to ${to}`,
      });

      const logs = await fetchNotificationHistory().catch(() => null);
      if (logs) setHistory(logs);

      toast.showSaved(`Test email sent to ${to} — check Inbox / Spam`);
    } catch (err) {
      const errMsg = err.message || 'Test email failed';
      toast.showError(errMsg);
      try {
        await logPlatformNotification({
          recipient: to,
          gymName: 'Platform',
          type: 'test_email',
          channel: 'email',
          status: 'failed',
          message: errMsg,
        });
        const logs = await fetchNotificationHistory().catch(() => null);
        if (logs) setHistory(logs);
      } catch {
        /* ignore */
      }
    } finally {
      setAutoTestSending(false);
    }
  };

  const fillResendPreset = () => {
    setConnectForm((prev) => ({
      ...prev,
      host: 'smtp.resend.com',
      port: '587',
      encryption: 'tls',
      username: prev.username?.trim() || 'resend',
    }));
  };

  return (
    <div>
      <PageHeader
        title={activeTab === 'history' ? 'Notifications' : 'Reminder Settings'}
        subtitle={
          activeTab === 'history'
            ? 'Reminder history & delivery status for gym subscriptions.'
            : 'Subscription reminders and email automation.'
        }
        variant="banner"
      >
        <div className="rec-notif-header-actions">
          {globalConfig.emailConnected ? (
            <>
              <button
                type="button"
                className="owner-btn owner-btn-primary"
                onClick={handleSendTestEmail}
                disabled={autoTestSending}
              >
                <MdBolt style={{ marginRight: 6 }} />
                {autoTestSending ? 'Sending test…' : 'Test Email'}
              </button>
              <span className="owner-badge owner-badge-success">
                Email connected · {globalConfig.smtp?.fromEmail || globalConfig.emailSource}
              </span>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={handleOpenConnectModal}>
                Manage Email
              </button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={handleDisconnectEmail}>
                Disconnect Email
              </button>
            </>
          ) : (
            <button type="button" className="owner-btn owner-btn-primary" onClick={handleOpenConnectModal}>
              <MdEmail style={{ marginRight: 8 }} />
              Connect Email
            </button>
          )}

          {globalConfig.whatsappConnected ? (
            <>
              <span className="owner-badge owner-badge-success">
                WhatsApp connected · {globalConfig.whatsappPhone}
              </span>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={handleOpenWhatsAppModal}>
                Manage WhatsApp
              </button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={handleDisconnectWhatsApp}>
                Disconnect WhatsApp
              </button>
            </>
          ) : (
            <button type="button" className="owner-btn owner-btn-primary" onClick={handleOpenWhatsAppModal}>
              <FaWhatsapp style={{ marginRight: 8 }} />
              Connect WhatsApp
            </button>
          )}
        </div>
      </PageHeader>

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading notifications...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="rec-notif-tabs">
        <button
          type="button"
          className={`rec-notif-tab${activeTab === 'history' ? ' active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <MdNotifications style={{ marginRight: 6, verticalAlign: -3 }} />
          Notifications
        </button>
        <button
          type="button"
          className={`rec-notif-tab${activeTab === 'settings' ? ' active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <MdSettings style={{ marginRight: 6, verticalAlign: -3 }} />
          Reminder Settings
        </button>
      </div>

      {activeTab === 'history' ? (
        <>
          <div className="rec-notif-filters">
            <div className="rec-notif-filter-group">
              <label htmlFor="notif-status-filter">Status</label>
              <select
                id="notif-status-filter"
                className="owner-form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusFilterOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="rec-notif-filter-group">
              <label htmlFor="notif-channel-filter">Channel</label>
              <select
                id="notif-channel-filter"
                className="owner-form-select"
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value)}
              >
                {channelFilterOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="rec-notif-filter-group">
              <label htmlFor="notif-type-filter">Type</label>
              <select
                id="notif-type-filter"
                className="owner-form-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                {typeFilterOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <button type="button" className="owner-btn owner-btn-primary" onClick={handleApplyFilters}>
              <MdFilterList style={{ marginRight: 6 }} />
              Apply
            </button>
          </div>

          <div className="owner-card rec-panel-card rec-member-list-card rec-notif-table-card">
            <div className="owner-table-wrap">
              <table className="owner-table rec-member-table rec-notif-table">
                <thead>
                  <tr>
                    <th>Gym</th>
                    <th>Type</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th>Sent At</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.length > 0 ? (
                    filteredHistory.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div>{item.member}</div>
                          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{item.recipient}</div>
                        </td>
                        <td>{item.typeLabel}</td>
                        <td style={{ textTransform: 'capitalize' }}>{item.channel}</td>
                        <td>
                          <span className={`owner-badge ${statusBadge[item.status] || 'owner-badge-info'}`}>
                            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                          </span>
                        </td>
                        <td>{item.sentAt}</td>
                        <td>
                          <button
                            type="button"
                            className="owner-btn owner-btn-secondary owner-btn-sm"
                            onClick={() => setViewTarget(item)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="owner-placeholder-text">No notifications match your filters.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="owner-card rec-notif-section-card">
            <div className="rec-notif-global-config">
              <h3 className="rec-notif-config-heading">
                <MdSettings className="rec-notif-config-heading-icon" />
                Global Config
              </h3>

              <div className="rec-notif-toggle-row">
                <div>
                  <div className="rec-notif-toggle-label">Enable Email Delivery</div>
                  <div className="rec-notif-toggle-desc">Route notifications via email</div>
                </div>
                <BigToggle
                  checked={globalConfig.emailEnabled}
                  onChange={(emailEnabled) => updateGlobal({ emailEnabled })}
                  ariaLabel="Enable email delivery"
                />
              </div>

              <div className="rec-notif-toggle-row">
                <div>
                  <div className="rec-notif-toggle-label">Enable WhatsApp Delivery</div>
                  <div className="rec-notif-toggle-desc">Route notifications via WhatsApp</div>
                </div>
                <BigToggle
                  checked={globalConfig.whatsappEnabled}
                  onChange={(whatsappEnabled) => updateGlobal({ whatsappEnabled })}
                  ariaLabel="Enable WhatsApp delivery"
                />
              </div>

              <div className="owner-form-group owner-mt-20">
                <label className="owner-form-label" htmlFor="email-source">Sender Email Address</label>
                <select
                  id="email-source"
                  className="owner-form-select"
                  value={globalConfig.emailSource}
                  onChange={(e) => updateGlobal({ emailSource: e.target.value })}
                >
                  {emailSourceOptions.map((opt) => (
                    <option key={opt.value || 'auto'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div className="rec-notif-field-hint">
                  Select the sender address used for automated gym reminders.
                </div>
              </div>

              <div className="owner-grid-2 owner-mt-20">
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="default-send-time">Default Send Time</label>
                  <input
                    id="default-send-time"
                    type="time"
                    className="owner-form-input"
                    value={globalConfig.defaultSendTime}
                    onChange={(e) => updateGlobal({ defaultSendTime: e.target.value })}
                  />
                  <div className="rec-notif-field-hint">Cron dispatches at configured time for each rule.</div>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="owner-email">
                    <MdPerson style={{ marginRight: 4, verticalAlign: -3 }} />
                    Owner Email
                  </label>
                  <input
                    id="owner-email"
                    type="email"
                    className="owner-form-input"
                    placeholder="owner@gympro.io"
                    value={globalConfig.adminEmail}
                    onChange={(e) => updateGlobal({ adminEmail: e.target.value })}
                  />
                  <div className="rec-notif-field-hint">
                    Platform owner email for system notifications and invoice copies.
                  </div>
                </div>
              </div>

              <div className="rec-notif-toggle-row owner-mt-20">
                <div>
                  <div className="rec-notif-toggle-label">
                    <MdSend style={{ marginRight: 4, verticalAlign: -3 }} />
                    Subscription Invoice Copy
                  </div>
                  <div className="rec-notif-toggle-desc">
                    <MdInfoOutline style={{ marginRight: 4, verticalAlign: -2 }} />
                    Set the owner email address first to enable this toggle.
                  </div>
                </div>
                <BigToggle
                  checked={globalConfig.sendInvoiceCopyToOwner}
                  onChange={(sendInvoiceCopyToOwner) => updateGlobal({ sendInvoiceCopyToOwner })}
                  ariaLabel="Send invoice copy to owner"
                />
              </div>

              <div className="rec-notif-toggle-row">
                <div>
                  <div className="rec-notif-toggle-label">
                    <MdBolt style={{ marginRight: 4, verticalAlign: -3 }} />
                    Auto-send on Save
                  </div>
                  <div className="rec-notif-toggle-desc">
                    When ON, the post-save modal is skipped and the invoice fires immediately. When OFF, the modal appears to review and send manually.
                  </div>
                </div>
                <BigToggle
                  checked={globalConfig.autoSendOnSave}
                  onChange={(autoSendOnSave) => updateGlobal({ autoSendOnSave })}
                  ariaLabel="Auto-send on save"
                />
              </div>
            </div>
          </div>

          <div className="rec-notif-section-card">
            <h3 className="rec-notif-section-title">
              <MdBusiness className="rec-notif-section-icon" />
              Gym Reminders
            </h3>
            <div className="rec-notif-reminder-list">
              {gymReminders.map((item) => (
                <ReminderRow
                  key={item.id}
                  item={item}
                  expanded={expandedId === item.id}
                  onToggleExpand={toggleExpand}
                  onUpdate={updateGymReminder}
                />
              ))}
            </div>
            <div className="settings-save-bar owner-mt-20">
              <button type="button" className="owner-btn owner-btn-primary" onClick={handleSaveSettings} disabled={settingsSaving || loading}>
                {settingsSaving ? 'Saving…' : 'Save Reminder Settings'}
              </button>
            </div>
          </div>
        </>
      )}

      {viewTarget && (
        <div className="subscription-modal-overlay" onClick={() => setViewTarget(null)}>
          <div
            className="subscription-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="view-email-title"
          >
            <div className="subscription-modal-header">
              <h2 id="view-email-title" className="subscription-modal-title">Email Message</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setViewTarget(null)} aria-label="Close">×</button>
            </div>
            <div className="subscription-form">
              <div className="owner-form-group">
                <label className="owner-form-label">Recipient</label>
                <div className="owner-form-input" style={{ background: 'var(--color-bg-dark)', cursor: 'default' }}>
                  {viewTarget.member} · {viewTarget.recipient}
                </div>
              </div>
              <div className="owner-grid-2">
                <div className="owner-form-group">
                  <label className="owner-form-label">Type</label>
                  <div className="owner-form-input" style={{ background: 'var(--color-bg-dark)', cursor: 'default' }}>
                    {viewTarget.typeLabel}
                  </div>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label">Status</label>
                  <div className="owner-form-input" style={{ background: 'var(--color-bg-dark)', cursor: 'default' }}>
                    {viewTarget.status.charAt(0).toUpperCase() + viewTarget.status.slice(1)}
                  </div>
                </div>
              </div>
              <div className="owner-form-group">
                <label className="owner-form-label">Message</label>
                <textarea
                  className="owner-form-input"
                  rows={5}
                  readOnly
                  value={viewTarget.message}
                  style={{ resize: 'vertical', background: 'var(--color-bg-dark)' }}
                />
              </div>
              <div className="subscription-form-actions">
                <button type="button" className="owner-btn owner-btn-primary" onClick={() => setViewTarget(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConnectModal && (
        <div className="subscription-modal-overlay" onClick={() => setShowConnectModal(false)}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="connect-email-title"
          >
            <div className="subscription-modal-header">
              <h2 id="connect-email-title" className="subscription-modal-title">
                {globalConfig.emailConnected ? 'Manage Email Connection' : 'Connect Email'}
              </h2>
              <button type="button" className="subscription-modal-close" onClick={() => setShowConnectModal(false)} aria-label="Close">×</button>
            </div>
            <form className="subscription-form" onSubmit={handleConnectEmail}>
              <div className="owner-grid-2">
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-smtp-host">SMTP Host</label>
                  <input
                    id="connect-smtp-host"
                    name="host"
                    className="owner-form-input"
                    value={connectForm.host}
                    onChange={handleConnectFieldChange}
                    placeholder="smtp.gmail.com or smtp.resend.com"
                    required
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-smtp-port">SMTP Port</label>
                  <input
                    id="connect-smtp-port"
                    name="port"
                    className="owner-form-input"
                    value={connectForm.port}
                    onChange={handleConnectFieldChange}
                    placeholder="587"
                    required
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-smtp-encryption">Encryption</label>
                  <select
                    id="connect-smtp-encryption"
                    name="encryption"
                    className="owner-form-select"
                    value={connectForm.encryption}
                    onChange={handleConnectFieldChange}
                  >
                    <option value="tls">TLS</option>
                    <option value="ssl">SSL</option>
                    <option value="none">None</option>
                  </select>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-smtp-username">SMTP Username</label>
                  <input
                    id="connect-smtp-username"
                    name="username"
                    className="owner-form-input"
                    value={connectForm.username}
                    onChange={handleConnectFieldChange}
                    placeholder="you@gmail.com or resend"
                    autoComplete="off"
                    required
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-smtp-password">SMTP Password</label>
                  <input
                    id="connect-smtp-password"
                    name="password"
                    type="password"
                    className="owner-form-input"
                    value={connectForm.password}
                    onChange={handleConnectFieldChange}
                    placeholder="App Password or re_ API key"
                    autoComplete="new-password"
                    required
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-from-name">From Name</label>
                  <input
                    id="connect-from-name"
                    name="fromName"
                    className="owner-form-input"
                    value={connectForm.fromName}
                    onChange={handleConnectFieldChange}
                    placeholder="GymPro Software"
                  />
                </div>
                <div className="owner-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="owner-form-label" htmlFor="connect-from-email">From Email</label>
                  <input
                    id="connect-from-email"
                    name="fromEmail"
                    type="email"
                    className="owner-form-input"
                    value={connectForm.fromEmail}
                    onChange={handleConnectFieldChange}
                    placeholder="notifications@yourdomain.com"
                    required
                  />
                </div>
              </div>

              {connectError && <p className="membership-modal-message" style={{ color: 'var(--color-danger)' }}>{connectError}</p>}

              <div className="subscription-form-actions">
                <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setShowConnectModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="owner-btn owner-btn-primary" disabled={connectSaving}>
                  {connectSaving ? 'Connecting...' : globalConfig.emailConnected ? 'Save Changes' : 'Connect Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showWhatsAppModal && (
        <div className="subscription-modal-overlay" onClick={() => setShowWhatsAppModal(false)}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="connect-whatsapp-title"
          >
            <div className="subscription-modal-header">
              <h2 id="connect-whatsapp-title" className="subscription-modal-title">
                {globalConfig.whatsappConnected ? 'Manage WhatsApp Connection' : 'Connect WhatsApp'}
              </h2>
              <button type="button" className="subscription-modal-close" onClick={() => setShowWhatsAppModal(false)} aria-label="Close">×</button>
            </div>
            <form className="subscription-form" onSubmit={handleConnectWhatsApp}>
              <p className="rec-notif-field-hint" style={{ marginBottom: 16 }}>
                Enter your WhatsApp Business API credentials to send notifications via WhatsApp.
              </p>
              <div className="owner-grid-2">
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-wa-business">Business Name</label>
                  <input
                    id="connect-wa-business"
                    name="businessName"
                    className="owner-form-input"
                    value={whatsappForm.businessName}
                    onChange={handleWhatsAppFieldChange}
                    placeholder="Gym CRM"
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-wa-phone">Business Phone Number</label>
                  <input
                    id="connect-wa-phone"
                    name="phoneNumber"
                    className="owner-form-input"
                    value={whatsappForm.phoneNumber}
                    onChange={handleWhatsAppFieldChange}
                    placeholder="+92 300 1112233"
                    required
                  />
                </div>
                <div className="owner-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="owner-form-label" htmlFor="connect-wa-api-key">API Key</label>
                  <input
                    id="connect-wa-api-key"
                    name="apiKey"
                    type="password"
                    className="owner-form-input"
                    value={whatsappForm.apiKey}
                    onChange={handleWhatsAppFieldChange}
                    placeholder="Enter WhatsApp API key"
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              {whatsappError && <p className="membership-modal-message" style={{ color: 'var(--color-danger)' }}>{whatsappError}</p>}

              <div className="subscription-form-actions">
                <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setShowWhatsAppModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="owner-btn owner-btn-primary" disabled={whatsappSaving}>
                  {whatsappSaving ? 'Connecting...' : globalConfig.whatsappConnected ? 'Save Changes' : 'Connect WhatsApp'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Notifications;
